import React, { useState, useEffect, useRef } from 'react';
import SecondaryButton from '../../button/SecondaryButton';
import ProgressIndicator from '../../animation/ProgressIndicator';
import './InteractiveNav.css';

/**
 * InteractiveNav Component
 * Navigation panel with progress indicators and auto-cycling
 * Standardized component using SecondaryButton and ProgressIndicator
 * 
 * @param {Array} items - Array of {value: string, label: string}
 * @param {string} activeItem - Currently active item value
 * @param {function} onItemClick - Callback when item is clicked: (value: string) => void
 * @param {boolean} autoCycle - Enable automatic cycling (default: true)
 * @param {number} cycleDuration - Duration for each cycle in ms (default: 10000)
 * @param {string} orientation - Layout orientation: 'vertical' | 'horizontal' (default: 'vertical')
 * @param {string} className - Additional CSS classes
 */
const InteractiveNav = ({
  items = [],
  activeItem,
  onItemClick,
  autoCycle = true,
  cycleDuration = 10000,
  orientation = 'vertical',
  className = ''
}) => {
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const cyclingTimeoutRef = useRef(null);
  const progressTimeoutRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // Auto-cycling effect - handles progress animation and auto-advance
  useEffect(() => {
    if (!autoCycle || items.length === 0) return;

    const currentIndex = items.findIndex(item => item.value === activeItem);
    if (currentIndex === -1) return;

    // Clear any existing timeouts/intervals
    if (progressTimeoutRef.current) {
      clearTimeout(progressTimeoutRef.current);
    }
    if (progressIntervalRef.current) {
      cancelAnimationFrame(progressIntervalRef.current);
    }

    // Reset progress to 0 immediately
    setProgressValue(0);

    // If user is interacting, don't auto-advance, just show progress
    if (isUserInteracting) {
      // User clicked, show progress but don't auto-advance
      const startTime = Date.now();
      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / cycleDuration) * 100, 100);
        setProgressValue(progress);

        if (progress < 100) {
          progressIntervalRef.current = requestAnimationFrame(updateProgress);
        } else {
          // When progress completes, allow automatic cycling to resume
          setIsUserInteracting(false);
        }
      };

      progressTimeoutRef.current = setTimeout(() => {
        updateProgress();
      }, 100);
    } else {
      // Auto-cycling mode - show progress and advance to next
      const nextIndex = (currentIndex + 1) % items.length;

      const startTime = Date.now();
      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / cycleDuration) * 100, 100);
        setProgressValue(progress);

        if (progress < 100) {
          progressIntervalRef.current = requestAnimationFrame(updateProgress);
        } else {
          // When progress completes, switch to next content
          if (!isUserInteracting && onItemClick) {
            onItemClick(items[nextIndex].value);
          }
        }
      };

      progressTimeoutRef.current = setTimeout(() => {
        updateProgress();
      }, 100);
    }

    return () => {
      if (progressTimeoutRef.current) {
        clearTimeout(progressTimeoutRef.current);
      }
      if (progressIntervalRef.current) {
        cancelAnimationFrame(progressIntervalRef.current);
      }
    };
  }, [activeItem, items, autoCycle, isUserInteracting, cycleDuration, onItemClick]);

  // Click handler - user takes control
  const handleItemClick = (value) => {
    // Clear any existing timeouts/intervals
    if (progressTimeoutRef.current) {
      clearTimeout(progressTimeoutRef.current);
    }
    if (progressIntervalRef.current) {
      cancelAnimationFrame(progressIntervalRef.current);
    }
    
    // Set user interaction flag - this will trigger useEffect to show progress
    setIsUserInteracting(true);
    
    // Reset progress immediately
    setProgressValue(0);
    
    // Switch to clicked content
    if (onItemClick) {
      onItemClick(value);
    }
    // The useEffect will handle starting the progress animation
  };

  const orientationClass = orientation === 'horizontal' ? 'interactive-nav-horizontal' : 'interactive-nav-vertical';
  
  return (
    <div className={`interactive-nav ${className}`.trim()}>
      <div className={`interactive-nav-panel ${orientationClass}`}>
        {items.map((item) => {
          const isActive = activeItem === item.value;
          // eslint-disable-next-line no-unused-vars
          const showProgress = isActive && autoCycle;
          
          return (
            <div
              key={item.value}
              className={`interactive-nav-item ${isActive ? 'active' : ''}`}
              data-value={item.value}
            >
              <SecondaryButton
                variant="outline"
                size="small"
                onClick={() => handleItemClick(item.value)}
                className={`interactive-nav-button ${isActive ? 'active' : ''}`.trim()}
              >
                <span className="interactive-nav-label">{item.label}</span>
              </SecondaryButton>
              {isActive && autoCycle && (
                <div className="interactive-nav-progress">
                  <ProgressIndicator
                    progress={progressValue}
                    duration={cycleDuration}
                    monochrome={false}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InteractiveNav;

